import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { messagingActions } from '../../../reducers/messagingSlice';
import {
  selectFeatureFlagHitl3,
  selectAgentResponseTimeLimitSeconds,
} from '../../../redux/features/config/config.selector';
import { selectIsAIAutoMode } from '../../../redux/features/ai/ai.selector';
import {
  selectAwaitingCustomerUtterance,
  selectIsCarPresent,
} from '../../../selectors/message';

/**
 * Emits an `agent_metric` websocket event (`data.metric: 'agent_response_time'`)
 * the moment the agent's response time for a customer utterance EXCEEDS the
 * configured limit (AGENT_RESPONSE_TIME_LIMIT_SECONDS, default 6s), while the
 * agent has intercepted a takeover.
 *
 * Fires at most ONCE per customer utterance (keyed on `awaiting_since`), so it
 * fires again for each subsequent slow turn — i.e. it can fire multiple times
 * across an order.
 *
 * Lives here (mounted in MainPageLayout) rather than in ResponseTimeTile so the
 * metric is emitted regardless of whether the Pulse card is expanded or
 * collapsed. hitl_3_0-only: MainPageLayout is the flag's subtree, and the hook
 * also guards on the flag defensively.
 */
const useAgentResponseTimeMetric = () => {
  const dispatch = useAppDispatch();
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const limitSeconds = useAppSelector(selectAgentResponseTimeLimitSeconds);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const awaiting = useAppSelector(selectAwaitingCustomerUtterance);

  // Mirror the Response time tile's visibility EXACTLY so the metric fires
  // whenever that meter is live and crosses the limit: hitl_3_0 on, the agent has
  // intercepted (the AI left auto mode — `!isAIAutoMode`, set by the interception
  // action), and a car is present. `isCarPresent` (car_enter → true / car_exit →
  // false) is deliberate — it's the same gate the tile uses, and unlike the
  // transient `selectIsCarExited` it does not flip back after Finish/Cancel.
  const agentIntercepted = !isAIAutoMode;
  const active =
    isHitl3 && agentIntercepted && isCarPresent && limitSeconds > 0;

  // The awaiting_since we've already emitted for — so each utterance fires once.
  const firedForSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active || awaiting == null) {
      return undefined;
    }
    const { sinceMs, text } = awaiting;
    if (firedForSinceRef.current === sinceMs) {
      return undefined; // already emitted for this utterance
    }

    const limitMs = limitSeconds * 1000;

    const fire = () => {
      if (firedForSinceRef.current === sinceMs) return;
      firedForSinceRef.current = sinceMs;
      dispatch(
        messagingActions.sendAgentMetric({
          data: {
            metric: 'agent_response_time',
            status: 'EXCEEDED',
            threshold_ms: limitMs,
            awaiting_since: new Date(sinceMs).toISOString(),
            customer_utterance: text,
          },
        })
      );
    };

    // Fire exactly when the limit is crossed. If we mounted / became active
    // already past it, fire immediately.
    const remainingMs = sinceMs + limitMs - Date.now();
    if (remainingMs <= 0) {
      fire();
      return undefined;
    }
    const timerId = setTimeout(fire, remainingMs);
    return () => clearTimeout(timerId);
  }, [active, awaiting, limitSeconds, dispatch]);
};

export default useAgentResponseTimeMetric;
